import type { PrismaClient, ShareLink } from '@prisma/client';
import { randomToken, sha256 } from '../../core/crypto.js';

export const SHARE_TTL_DAYS = 60;

/**
 * Creates a public read-only link. The token is 128 bits of randomness; only its SHA-256 is stored, so
 * a database leak does not expose working links. Each call makes a new link (old ones keep working
 * until they expire or are revoked).
 */
export async function createShareLink(
  db: PrismaClient,
  args: {
    companyId: number;
    entityType: 'sales_document' | 'receipt' | 'purchase_order' | 'payout';
    entityId: number;
    userId?: number | null;
    ttlDays?: number;
  },
): Promise<{ token: string; expiresAt: Date }> {
  const token = randomToken(16);
  const expiresAt = new Date(Date.now() + (args.ttlDays ?? SHARE_TTL_DAYS) * 86_400_000);
  await db.shareLink.create({
    data: {
      companyId: args.companyId,
      entityType: args.entityType,
      entityId: args.entityId,
      tokenHash: sha256(token),
      expiresAt,
      createdBy: args.userId ?? null,
    },
  });
  return { token, expiresAt };
}

export const shareUrl = (appUrl: string, token: string) =>
  `${appUrl.replace(/\/$/, '')}/p/${token}`;

/** Returns the link if the token is valid, unexpired and not revoked. */
export async function resolveShareLink(db: PrismaClient, token: string): Promise<ShareLink | null> {
  if (!/^[A-Za-z0-9_-]{20,64}$/.test(token)) return null;
  const link = await db.shareLink.findUnique({ where: { tokenHash: sha256(token) } });
  if (!link || link.revokedAt) return null;
  if (link.expiresAt && link.expiresAt < new Date()) return null;
  return link;
}

export const revokeShareLinks = (db: PrismaClient, entityType: string, entityId: number) =>
  db.shareLink.updateMany({
    where: { entityType, entityId, revokedAt: null },
    data: { revokedAt: new Date() },
  });
